import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { BookOpen, ChartColumn, MessageSquare } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { storeToken } from '../../api/client';
import { teacherLogin } from '../../api/teachers';
import { useI18n } from '../../i18n/I18nContext';
import AuthLayout from '../Login/AuthLayout';
import AuthField from '../Login/AuthField';
import AuthAlert from '../Login/AuthAlert';
import AuthSubmit from '../Login/AuthSubmit';
import useSignedInTarget from '../Login/useSignedInTarget';
import { authErrorMessage, canAutoFocus, focusField } from '../Login/authHelpers';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// what a phone number looks like when typed into the email field
const PHONE_LIKE = /^\+?[\d\s()-]{7,}$/;

function TeacherLogin() {
  const { t } = useI18n();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({}); // { identifier, password }
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [shake, setShake] = useState(false);
  const navigate = useNavigate();
  const { restoreSession } = useAuth();
  // Already signed in: no form, straight to where signing in would lead.
  const signedInTarget = useSignedInTarget({ busy: submitting });

  if (signedInTarget) return <Navigate to={signedInTarget} replace />;

  const fail = (message) => {
    setError(message);
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShake(false);

    const email = identifier.trim();
    const missing = {};
    if (!EMAIL_RE.test(email)) {
      // the long explanation is for someone who typed a phone number; an empty or half-typed
      // address just needs the short ask
      missing.identifier = PHONE_LIKE.test(email)
        ? t('teacherAdmin.login.emailOnlyError')
        : t('auth.login.errorEmail');
    }
    if (!password) missing.password = t('auth.login.errorPassword');
    setFieldErrors(missing);
    if (missing.identifier || missing.password) {
      focusField(missing.identifier ? 'identifier' : 'password');
      return;
    }

    setSubmitting(true);

    try {
      const response = await teacherLogin({ email, password });

      if (response?.accessToken) {
        storeToken('accessToken', response.accessToken);
        if (response.refreshToken) storeToken('refreshToken', response.refreshToken);
        await restoreSession();
        navigate('/teacher/dashboard', { replace: true });
      } else {
        fail(t('teacherAdmin.login.loginFailed'));
      }
    } catch (err) {
      fail(authErrorMessage(err, {
        badCredentials: t('teacherAdmin.login.loginFailed'),
        fallback: t('teacherAdmin.login.loginFailed'),
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    { icon: <BookOpen size={18} />, title: t('teacherAdmin.login.feature1Title'), desc: t('teacherAdmin.login.feature1Desc') },
    { icon: <ChartColumn size={18} />, title: t('teacherAdmin.login.feature2Title'), desc: t('teacherAdmin.login.feature2Desc') },
    { icon: <MessageSquare size={18} />, title: t('teacherAdmin.login.feature3Title'), desc: t('teacherAdmin.login.feature3Desc') },
  ];

  return (
    <AuthLayout
      title={t('teacherAdmin.login.title')}
      tagline={t('teacherAdmin.login.tagline1')}
      taglineEm={t('teacherAdmin.login.taglineEm')}
      desc={t('teacherAdmin.login.desc')}
      features={features}
      shake={shake}
      below={[
        t('teacherAdmin.login.noAccount'),
        <>{t('auth.login.staffPrompt')} <Link to="/login">{t('auth.login.staffLink')}</Link></>,
      ]}
    >
      <div className="auth-form-header">
        <h1>{t('teacherAdmin.login.title')}</h1>
        <p>{t('teacherAdmin.login.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} noValidate aria-busy={submitting}>
        <AuthField
          id="identifier"
          type="email"
          label={t('teacherAdmin.login.email')}
          value={identifier}
          onChange={(e) => {
            setIdentifier(e.target.value);
            setError('');
            setFieldErrors((current) => ({ ...current, identifier: undefined }));
          }}
          placeholder={t('auth.login.identifierPlaceholder')}
          error={fieldErrors.identifier}
          invalid={!!error}
          describedBy="teacher-login-error"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus={canAutoFocus()}
          readOnly={submitting}
        />

        <AuthField
          id="password"
          type="password"
          label={t('auth.login.passwordLabel')}
          aside={(
            <Link to="/forgot-password" state={{ backTo: '/teacher-login' }} className="auth-forgot-link">
              {t('auth.login.forgotPassword')}
            </Link>
          )}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError('');
            setFieldErrors((current) => ({ ...current, password: undefined }));
          }}
          placeholder={t('auth.login.passwordPlaceholder')}
          error={fieldErrors.password}
          invalid={!!error}
          describedBy="teacher-login-error"
          autoComplete="current-password"
          readOnly={submitting}
        />

        <AuthAlert id="teacher-login-error">{error}</AuthAlert>

        <AuthSubmit busy={submitting} label={t('auth.login.signIn')} busyLabel={t('auth.login.signingIn')} />
      </form>
    </AuthLayout>
  );
}

export default TeacherLogin;
